import React, { useRef, useEffect, useState, useMemo } from 'react';
import { getImagePath } from '../utils/imageRegistry';

const CreatorCarousel = ({ teams }) => {
    const scrollRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [timerTrigger, setTimerTrigger] = useState(0);

    const shuffledCreators = useMemo(() => {
        return [...teams]
            .filter(t => t.name)
            .sort(() => Math.random() - 0.5);
    }, [teams]);

    const displayList = useMemo(() => {
        if (shuffledCreators.length === 0) return [];
        return [...shuffledCreators, shuffledCreators[0]];
    }, [shuffledCreators]);

    const handleScroll = () => {
        if (!scrollRef.current) return;
        const container = scrollRef.current;
        const width = container.offsetWidth;
        const scrollPos = container.scrollLeft;
        const index = Math.round(scrollPos / width);
        
        // Instant reset when landing on the clone
        if (index === shuffledCreators.length) {
            container.scrollTo({ left: 0, behavior: 'auto' });
            setActiveIndex(0);
        } else {
            setActiveIndex(index % shuffledCreators.length);
        }
    };

    const scrollToCreator = (targetIndex, isManual = false) => {
        if (!scrollRef.current) return;
        const container = scrollRef.current;
        const width = container.offsetWidth;
        const currentIndex = Math.round(container.scrollLeft / width);

        if (isManual) setTimerTrigger(prev => prev + 1);
        if (targetIndex === currentIndex) return;

        // LOGIC: Smooth if adjacent, Instant if jump
        const isAdjacent = Math.abs(targetIndex - currentIndex) === 1;// || 
                           //(currentIndex === shuffledCreators.length - 1 && targetIndex === 0);

        container.scrollTo({ 
            left: targetIndex * width, 
            behavior: isAdjacent ? 'smooth' : 'auto' 
        });
    };

    useEffect(() => {
        if (shuffledCreators.length === 0) return;

        const interval = setInterval(() => {
            if (document.hidden) return; 

            if (scrollRef.current) {
                const container = scrollRef.current;
                const width = container.offsetWidth;
                const currentIndex = Math.round(container.scrollLeft / width);
                
                // Auto-scroll is always a single step, so it stays smooth
                container.scrollTo({
                    left: (currentIndex + 1) * width,
                    behavior: 'smooth'
                });
            }
        }, 15000); 

        return () => clearInterval(interval);
    }, [shuffledCreators.length, timerTrigger]); 

    if (shuffledCreators.length === 0) return null;

    return (
        <div className="GlassCard" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            
            {/* LOGO NAVIGATION BAR */}
            <div className="carousel-top-bar" style={{ 
                padding: '15px 20px', 
                background: 'rgba(0,0,0,0.2)', 
                borderBottom: '1px solid rgba(255,255,255,0.05)', 
                display: 'flex', 
                justifyContent: 'center' 
            }}>
                <div style={{ display: 'flex', gap: 25, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                    {shuffledCreators.map((c, idx) => (
                        <button
                            key={c.id}
                            onClick={() => scrollToCreator(idx, true)}
                            style={{
                                width: 38, height: 38, borderRadius: '50%',
                                border: activeIndex === idx ? '2px solid #FFD700' : '2px solid transparent',
                                background: c.color || '#333',
                                padding: 0, cursor: 'pointer', flexShrink: 0,
                                transition: 'all 0.3s ease',
                                transform: activeIndex === idx ? 'scale(1.2)' : 'scale(1)',
                                boxShadow: activeIndex === idx ? '0 0 15px rgba(255,215,0,0.3)' : 'none',
                                overflow: 'hidden',
                                opacity: activeIndex === idx ? 1 : 0.6
                            }}
                        >
                            {c.logoUrl ? (
                                <img src={getImagePath(c.logoUrl)} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                            ) : c.logo ? (
                                // Added SVG support for the small buttons
                                <img src={`data:image/svg+xml;utf8,${encodeURIComponent(c.logo)}`} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                            ) : (
                                <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize: 12}}>{c.name.charAt(0)}</div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* BIO SCROLL CONTAINER */}
            <div 
                ref={scrollRef}
                onScroll={handleScroll}
                style={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    // Note: We leave the global behavior as 'auto' and specify behavior in scrollTo calls
                    scrollBehavior: 'auto',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}
                className="HideScrollbar carousel-track"
            >
                <style>{`
                    .HideScrollbar::-webkit-scrollbar { display: none; }

                    /* MOBILE OVERRIDES */
                    @media (max-width: 768px) {
                        /* 1. Hide the top bar entirely */
                        .carousel-top-bar {
                            display: none !important; 
                        }

                        /* 2. Restrict to horizontal scroll, let it grow vertically */
                        .carousel-track {
                            overflow-y: visible !important; /* Kills the internal vertical scroll */
                            align-items: flex-start !important; /* Allows cards to stretch based on content */
                        }

                        /* 3. Let the card expand to fit the bio */
                        .carousel-card {
                            height: auto !important; 
                            max-height: none !important; 
                        }
                    }
                `}</style>

                {displayList.map((creator, idx) => (
                    <div 
                        key={`${creator.id}-${idx}`}
                        className="carousel-card"
                        style={{
                            flex: '0 0 100%',
                            scrollSnapAlign: 'start',
                            padding: '50px 30px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            boxSizing: 'border-box'
                        }}
                    >
                        <div style={{ 
                            width: 110, height: 110, borderRadius: '50%', marginBottom: 20, 
                            border: `3px solid ${creator.color || '#6C5DD3'}`,
                            padding: 4, background: 'rgba(255,255,255,0.05)'
                        }}>
                            <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#222' }}>
                                {creator.logoUrl ? (
                                    <img src={getImagePath(creator.logoUrl)} alt={creator.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : creator.logo ? (
                                    // Added SVG support for the large profile image
                                    <img src={`data:image/svg+xml;utf8,${encodeURIComponent(creator.logo)}`} alt={creator.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 36}}>{creator.name.charAt(0)}</div>
                                )}
                            </div>
                        </div>
                        
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '28px', color: '#fff' }}>{creator.name}</h4>
                        <p style={{ color: '#E0E0E0', lineHeight: '1.8', fontSize: '16px', margin: 0, maxWidth: 700 }}>
                            {creator.bio || `${creator.name} is competing for glory in Bridge Brawl 2026.`}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CreatorCarousel;